import { camelize } from 'helpers/syntaxHelper';
import { depthFirst } from 'helpers/treeHelpers';
import { dropRightWhile } from 'lodash';
import { ROOT_BREADCRUMB_VALUE } from '../components/constants';
import { getNavigationFilterForTree } from '../components/helpers/dataHelpers';
import { isTree } from '../components/helpers/datasetHelpers';
import { BreadCrumbNodeT, NavigationTree } from '../types';
import { TFunction } from 'i18next';

export const getInitialBreadcrumbs = (t: TFunction): BreadCrumbNodeT[] => [
    {
        value: ROOT_BREADCRUMB_VALUE,
        label: t(ROOT_BREADCRUMB_VALUE),
    },
];

/**
 * Check if the breadcrumb node is the last element of the breadcrumbs list.
 * @param {TreeT | undefined} breadcrumbNode the Node to check
 * @param {BreadCrumbNodeT[]} breadcrumbs the list of breadcrumb nodes
 * @returns {boolean}
 */
export const isNodeLastBreadcrumbElement = (
    breadcrumbNode: TreeT | undefined,
    breadcrumbs: BreadCrumbNodeT[],
): boolean => {
    if (breadcrumbs.length === 0) return false;
    return breadcrumbs[breadcrumbs.length - 1].value === breadcrumbNode?.value;
};

/**
 * Gets the navigation tree for the selected axis.
 * Charts can have multiple tree dimensions (orgCharts, mappings...), thus we need to find the tree corresponding the relevant axis on which the user want to dig.
 * @param {string | undefined} selectedAxisKey
 * @param {NavigationTree[] | undefined} navigationTrees - the navigation trees of the chart dimensions
 * @returns {NavigationTree | undefined}
 */
export const findNavigationTreeForSelectedAxis = (
    selectedAxisKey: string | undefined,
    navigationTrees: NavigationTree[] | undefined,
): NavigationTree | undefined =>
    selectedAxisKey
        ? navigationTrees?.find(
              (tree) => camelize(tree.field) === selectedAxisKey,
          )
        : undefined;

/**
 * Breadcrumb value can be a "simple" datum (Date, string or number) or an object with a value and a label.
 * This function returns the stringified value of the breadcrumb.
 * @param {PlainResponseDataValueT | DictAllDataValueT} breadcrumbValue - the breadcrumb value to stringify
 * @returns {string}
 */
const stringifyBreadcrumbValue = (breadcrumbValue: NotNullDataValueT) =>
    isTree(breadcrumbValue)
        ? breadcrumbValue.value.toString()
        : breadcrumbValue.toString();

/**
 * Checks if the breadcrumb value is the root breadcrumb value.
 * @param {ResponseDataValueT} breadcrumbValue - the breadcrumb value to check
 * @returns {boolean}
 */
export const getIsInitialBreadcrumb = (breadcrumbValue: ResponseDataValueT) =>
    breadcrumbValue &&
    stringifyBreadcrumbValue(breadcrumbValue) === ROOT_BREADCRUMB_VALUE;

/**
 * Finds the nodes corresponding to the breadcrumb value in the navigation tree.
 * @param {string} stringSelection - the breadcrumb value to find in the tree
 * @param {NavigationTree | undefined} tree - the navigation tree of the chart dimension if it exists.
 * @returns {TreeT[]}
 */
const getNodesFromValueOrLabel = (
    stringSelection: string,
    tree: TreeT | undefined,
): TreeT[] => {
    if (!stringSelection || !tree) return [];

    return depthFirst(tree).filter((node) =>
        [node.value, node.label].includes(stringSelection),
    );
};

/**
 * Finds the node corresponding to the breadcrumb value in the navigation tree.
 * @param {ResponseDataValueT} breadcrumbValue - the breadcrumb value to find in the tree
 * @param {NavigationTree | undefined} navigationTree - the navigation tree of the chart dimension if it exists.
 * @returns {TreeT | undefined} the node corresponding to the breadcrumb value if it exists.
 */
export const getCurrentBreadcrumbNode = (
    breadcrumbValue: ResponseDataValueT,
    navigationTree: NavigationTree | undefined,
): TreeT | undefined => {
    if (!breadcrumbValue || !navigationTree) {
        return undefined;
    }

    const stringBreadcrumbValue = stringifyBreadcrumbValue(breadcrumbValue);
    // FIXME for all viz but table, stringBreadcrumbValue is actually the label, which is not unique, as identifier:
    // we want to use the node.value, breadcrumbValue should not be a string but {value, label}
    // we need to update `formatVizData` to retain the value and labels for each data point, which would
    // entail an update for the corresponding Chart (BarChart, PieChart, ...)
    // https://linear.app/manty/issue/MAN-51/tech-debt-breadcrumbs-use-labels-to-identify-nodes-instead-of-values
    const currentNode = getNodesFromValueOrLabel(
        stringBreadcrumbValue,
        navigationTree.tree,
    )[0];

    return currentNode;
};

/**
 * Determines if the user click should trigger a breadcrumb or a raw data query.
 * - If their is no navigation tree associated to the selected axis, we do not need breadcrumbs.
 * - If the breadcrumb has no node in the navigation tree, it triggers a raw data query.
 * - If the node is at the end of the tree (i.e. it has no children), it triggers a raw data query.
 * - If the node has no subtree and its field is not in the query filters, it triggers a raw data query.
 * - Else, it triggers a breadcrumb query.
 * @param {TreeT | undefined} breadcrumbNode - the breadcrumb node to check
 * @param {NavigationTree | undefined} navigationTree - the navigation tree of the chart dimension if it exists.
 * @param {QueryFilterT[]} queryFilters - the chart's query filters
 * @returns {boolean}
 */
export const shouldTriggerBreadcrumbQuery = (
    breadcrumbNode: TreeT | undefined,
    navigationTree: NavigationTree | undefined,
    queryFilters: QueryFilterT[],
): boolean => {
    if (!breadcrumbNode || !navigationTree) {
        return false;
    }

    const hasNoChildren = breadcrumbNode.children.length === 0;
    if (hasNoChildren) return false;

    const navigationFiltr = getNavigationFilterForTree(
        navigationTree.field,
        breadcrumbNode,
        queryFilters,
    );

    if (navigationFiltr.value === '') {
        // Nothing to fetch, we do not trigger a query
        return false;
    }

    return true;
};

/**
 * Updates the chart's breadcrumbs with the new breadcrumb.
 * @param {BreadCrumbNodeT[]} existingBreadCrumbs - The chart's current breadcrumbs
 * @param {BreadCrumbNodeT} breadCrumb - The breadcrumb to add to the chart's breadcrumbs
 * @returns {BreadCrumbNodeT[]}
 */
const getNewBreadcrumbs = (
    existingBreadCrumbs: BreadCrumbNodeT[],
    breadCrumb: BreadCrumbNodeT,
): BreadCrumbNodeT[] => {
    let newBreadcrumbs = [...existingBreadCrumbs];
    if (newBreadcrumbs.find((bc) => bc.value === breadCrumb.value)) {
        // This was a click on existingBreadCrumbs, we remove all the elements after
        newBreadcrumbs = dropRightWhile(
            newBreadcrumbs,
            (b) => b.value !== breadCrumb.value,
        );
    } else {
        newBreadcrumbs.push(breadCrumb);
    }
    return newBreadcrumbs;
};

/**
 * Updates the chart's breadcrumbs and navigation filter with the new breadcrumb.
 * @param {TreeT} breadcrumbNode - The breadcrumb node to add to the chart's breadcrumbs
 * @param {BreadCrumbNodeT[]} breadcrumbs - The chart's current breadcrumbs
 * @param {NavigationTree} navigationTree - The navigation tree of the chart selected dimension
 * @param {QueryFilterT[]} queryFilters - The chart's query filters
 * @param {React.Dispatch<React.SetStateAction<BreadCrumbNodeT[]>>} setBreadcrumbs - setter of the chart's breadcrumbs
 * @param {React.Dispatch<React.SetStateAction<QueryFilterT | undefined>>} setNavigationFilter - setter of the chart's navigation filter. It is highly correlated with the breadcrumbs so we might want to merge them in the future.
 */
export const setNewBreadcrumbs = (
    breadcrumbNode: TreeT,
    breadcrumbs: BreadCrumbNodeT[],
    navigationTree: NavigationTree,
    queryFilters: QueryFilterT[],
    setBreadcrumbs: React.Dispatch<React.SetStateAction<BreadCrumbNodeT[]>>,
    setNavigationFilter: React.Dispatch<
        React.SetStateAction<QueryFilterT | undefined>
    >,
): void => {
    const newBreadcrumbs = getNewBreadcrumbs(breadcrumbs, {
        value: breadcrumbNode.value,
        label: breadcrumbNode.label,
        field: camelize(navigationTree.field),
    });
    setBreadcrumbs(newBreadcrumbs);
    setNavigationFilter(
        getNavigationFilterForTree(
            navigationTree.field,
            breadcrumbNode,
            queryFilters,
        ),
    );
};
